/**
 * SecurityCard Component
 *
 * Displays session security controls in Account Settings.
 * Shows the number of registered devices and allows the user to
 * revoke all sessions across all devices via "Logout from all devices".
 */

// 1. External dependencies
import React, { useState, useEffect, useCallback } from 'react';
import { ShieldAlert } from 'lucide-react';

// 2. Internal absolute imports
import { Card, CardBody, Button, useToastNotification } from '@enfomentis/ui';
import { useAuth } from '@/hooks/useAuth';
import { deviceService } from '@/services/DeviceService';

// 3. Relative imports
import { LogoutEverywhereModal } from './LogoutEverywhereModal';

/**
 * Security Card Component
 *
 * Fetches registered device count on mount and surfaces a
 * "Logout from all devices" action with a confirmation modal.
 */
export const SecurityCard: React.FC = () => {
  const { user, logoutEverywhere } = useAuth();
  const toast = useToastNotification();

  const [deviceCount, setDeviceCount] = useState(0);
  const [devicesLoaded, setDevicesLoaded] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Fetch device count on mount
  useEffect(() => {
    if (!user) return;

    let cancelled = false;

    deviceService.getUserDevices(user.id)
      .then(devices => {
        if (!cancelled) {
          setDeviceCount(devices.length);
          setDevicesLoaded(true);
        }
      })
      .catch(error => {
        console.error('❌ SecurityCard: Failed to fetch device count:', error);
        if (!cancelled) {
          setDevicesLoaded(true); // Stop showing loading state even on error
        }
      });

    return () => { cancelled = true; };
  }, [user]);

  const handleConfirm = useCallback(async () => {
    setIsLoggingOut(true);
    try {
      await logoutEverywhere();
      // Redirect is handled by AuthProvider — no further action needed here
    } catch (error) {
      console.error('❌ SecurityCard: logoutEverywhere failed:', error);
      toast.showToast({
        title: 'Sign out failed',
        description: 'Something went wrong. Please try again.',
        color: 'danger',
        timeout: 4000
      });
      setIsLoggingOut(false);
      setIsModalOpen(false);
    }
  }, [logoutEverywhere, toast]);

  const deviceLabel = !devicesLoaded
    ? 'Loading devices...'
    : deviceCount === 1
      ? '1 registered device'
      : `${deviceCount} registered devices`;

  return (
    <>
      <Card shadow="sm">
        <CardBody className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4 min-w-0 flex-1">
              <div className="text-danger flex-shrink-0">
                <ShieldAlert size={24} />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-lg font-semibold text-text-primary">
                  Active Sessions
                </h3>
                <p className="text-text-secondary">
                  {deviceLabel}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                color="danger"
                variant="light"
                size="lg"
                onPress={() => setIsModalOpen(true)}
                className="w-full sm:w-auto"
              >
                Logout from all devices
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>

      <LogoutEverywhereModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleConfirm}
        deviceCount={deviceCount}
        isLoading={isLoggingOut}
      />
    </>
  );
};
